import { Text } from "@hackthenorth/north";
import { DocumentPlusIcon } from "@heroicons/react/24/solid";
import { Button, Spinner } from "north.js";
import React, { Fragment, useEffect, useState } from "react";
import styled from "styled-components";

import {
  errorToast,
  Header,
  PageWrapper,
  successToast,
} from "src/shared/components";
import { EVENT_ID } from "src/shared/constants/event";
import { useUserContext } from "src/shared/contexts";
import { mediaQueries } from "src/theme/deviceBreakpoints";

import QuestCard from "../components/cards/QuestCard";
import CreateQuestModal from "../components/modals/organizer/quest/create";
import { useCreateGooseGameQuestMutation } from "../graphql/createGooseGameQuest.generated";
import { useGetAllGooseGameQuestsQuery } from "../graphql/getAllGooseGameQuests.generated";
import { useUpdateGooseGameQuestMutation } from "../graphql/updateGooseGameQuests.generated";
import { sortQuestsByVisibility } from "../questUtils";
import {
  GooseToQuestTypeMap,
  Quest,
  QuestToGooseTypeMap,
  QuestType,
} from "../types";

const GooseGamesQuests: React.FC = () => {
  const { isOrganizer } = useUserContext();
  const {
    data: getGooseGameQuestData,
    loading: fetchingQuests,
    refetch: refetchQuests,
  } = useGetAllGooseGameQuestsQuery({
    variables: { eventId: EVENT_ID },
  });

  const [createGooseGameQuest] = useCreateGooseGameQuestMutation();
  const [updateGooseGameQuest] = useUpdateGooseGameQuestMutation();

  const [gooseGameQuests, setGooseGameQuests] = useState<Quest[]>([]);
  const [isCreateQuestModalOpen, setIsCreateQuestModalOpen] = useState(false);

  const handleToggleHidden = async (quest: Quest, hidden: boolean) => {
    try {
      await updateGooseGameQuest({
        variables: {
          id: quest.id,
          title: quest.title,
          type: QuestToGooseTypeMap[quest.type],
          pointsRewarded: quest.points_rewarded,
          completionLimit: quest.completion_limit,
          questEventActivityCategory:
            quest.quest_event_activity_category ?? undefined,
          description: quest.description ?? undefined,
          questionString: quest.question_string ?? undefined,
          questionAnswer: quest.question_answer ?? undefined,
          flash: quest.flash,
          hidden,
        },
      });
      successToast(hidden ? "Quest hidden from hackers" : "Quest is visible");
    } catch (error) {
      errorToast("Failed to update quest visibility");
      console.error(error);
    } finally {
      refetchQuests();
    }
  };

  const handleCreateQuest = async (
    title: string,
    type: QuestType,
    pointsRewarded: number,
    completionLimit: number,
    successMessage: string,
    errorMessage: string,
    flash: boolean,
    hidden: boolean,
    eventCategory?: string,
    description?: string,
    questionString?: string,
    questionAnswer?: string,
    meta?: Record<string, unknown> | null
  ) => {
    try {
      await createGooseGameQuest({
        variables: {
          eventId: EVENT_ID,
          title: title,
          type: QuestToGooseTypeMap[type],
          pointsRewarded: pointsRewarded,
          completionLimit: completionLimit,
          questEventActivityCategory: eventCategory ?? undefined,
          description: description ?? undefined,
          questionString: questionString ?? undefined,
          questionAnswer: questionAnswer ?? undefined,
          flash: flash,
          hidden: hidden,
          meta,
        },
      });
      successToast(successMessage);
    } catch (error) {
      errorToast(errorMessage);
      console.error(error);
    } finally {
      refetchQuests();
    }
    setIsCreateQuestModalOpen(false);
  };

  useEffect(() => {
    if (getGooseGameQuestData && getGooseGameQuestData.gooseGameQuests) {
      const quests = getGooseGameQuestData.gooseGameQuests.map((quest) => ({
        ...quest,
        type: GooseToQuestTypeMap[quest.type],
        description: quest.description ?? undefined,
        quest_event_activity_category:
          quest.quest_event_activity_category ?? undefined,
        question_string: quest.question_string ?? undefined,
        question_answer: quest.question_answer ?? undefined,
      }));

      setGooseGameQuests(sortQuestsByVisibility(quests, isOrganizer));
    }
  }, [getGooseGameQuestData, isOrganizer]);

  return (
    <>
      <Header
        title="Goose Games"
        subtitle="Complete quests to collect points and redeem exclusive prizes!"
      />
      <StyledPageWrapper>
        <HeaderFlex>
          <TitleText mods="heading h3">Quests</TitleText>
          <Button
            leadingIcon={<DocumentPlusIcon width={12.8} color="white" />}
            size="md"
            color="primary"
            onClick={() => setIsCreateQuestModalOpen(true)}
          >
            Create new quest
          </Button>
        </HeaderFlex>
        <QuestCardGrid>
          {fetchingQuests ? (
            <Spinner />
          ) : (
            gooseGameQuests.map((quest) => (
              <Fragment key={quest.id}>
                <QuestCard
                  quest={quest}
                  onToggleHidden={handleToggleHidden}
                  refetchQuests={refetchQuests}
                />
              </Fragment>
            ))
          )}
        </QuestCardGrid>
      </StyledPageWrapper>

      {/* Modals */}
      <CreateQuestModal
        isOpen={isCreateQuestModalOpen}
        onConfirm={handleCreateQuest}
        onCancel={() => setIsCreateQuestModalOpen(false)}
      />
    </>
  );
};

const TitleText = styled(Text)`
  color: #374151 !important;
`;

const StyledPageWrapper = styled(PageWrapper)`
  background: ${({ theme }) => theme.globalConstants.color.backgroundLight};
`;

const HeaderFlex = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const QuestCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  padding-bottom: 24px;

  ${mediaQueries.medium} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${mediaQueries.largeMobile} {
    grid-template-columns: repeat(1, 1fr);
  }
`;

export default GooseGamesQuests;
